// src/pages/feedback/FeedbackDetailPage.tsx
//
// 글 상세 + 댓글.
//
// 데이터는 getPost(id) 한 번으로 본문과 댓글 전량을 받는다(서버가 Preload).
// 변경 액션 뒤에는 응답 DTO 로 부분 갱신하고, 댓글 삭제처럼 카운트가
// 흔들리는 경우만 재조회한다.
//
// 본문·댓글은 plain text 로만 렌더한다 — dangerouslySetInnerHTML 을 어떤
// 경로에서도 쓰지 않고, 줄바꿈은 CSS(white-space: pre-wrap)로만 처리한다.
// 마크다운/HTML 렌더링과 URL 자동 링크화는 범위 밖이다.
// TODO(feedback): 본문 URL 자동 링크 — sanitize 레이어 도입 후에만

import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";

import { designTokens } from "../../theme.ts";
import { fmtDateTime } from "../../utils/dateFormat.ts";
import { useFeedbackApi } from "../../hooks/useFeedbackApi.ts";
import {
  authorLabel,
  FeedbackPostDto,
  FeedbackStatus,
} from "../../contracts/feedback/types.ts";
import ConfirmDialog from "../estimates/ConfirmDialog.tsx";
import FeedbackStatusChip from "./components/FeedbackStatusChip.tsx";
import FeedbackStatusMenu from "./components/FeedbackStatusMenu.tsx";
import FeedbackTypeChip from "./components/FeedbackTypeChip.tsx";
import CommentComposer from "./components/CommentComposer.tsx";
import CommentList from "./components/CommentList.tsx";
import { statusLabel } from "./components/feedbackVisuals.ts";

const T = designTokens;

const C = {
  ink: "#171A1E",
  sub: "#6B7178",
  caption: "#9AA0A6",
  border: "#E4E7EA",
};

/**
 * "수정됨" 판정.
 *
 * 상태 변경(PATCH)도 서버의 updated_at 을 건드리므로 단순히
 * modified > created 로 보면 남이 상태를 바꾼 것만으로 "수정됨"이 붙는다.
 * 마지막 변경 시각이 statusChangedAt 과 같으면 본문 수정이 아니라고 본다.
 *
 * ── 알려진 한계(의도적으로 감수) ──
 * 이 판정은 타임스탬프 두 개의 비교라 "마지막 변경이 무엇이었나"만 알 수 있고
 * "수정된 적이 있나"는 알 수 없다. 서버 UpdateStatus 가 status_changed_at 과
 * updated_at 을 같은 트랜잭션에서 함께 갱신하므로, **본문을 실제로 고친 뒤
 * 누군가 상태를 옮기면 "수정됨" 배지가 사라지고 다시 돌아오지 않는다.**
 * 반대 방향(상태 변경만으로 "수정됨"이 잘못 붙는 것)보다 이쪽 오차를 택한
 * 것이며, 정확히 판정하려면 서버에 body_updated_at(또는 edit_count)이 필요하다.
 * TODO(feedback): 서버에 body_updated_at 추가 후 그 값으로 판정 교체
 */
function wasEdited(p: FeedbackPostDto): boolean {
  const created = Date.parse(p.createdTimestamp);
  const modified = Date.parse(p.modifiedTimestamp);
  if (Number.isNaN(created) || Number.isNaN(modified)) return false;
  if (modified - created <= 1000) return false;

  const statusAt = p.statusChangedAt ? Date.parse(p.statusChangedAt) : NaN;
  if (!Number.isNaN(statusAt) && Math.abs(modified - statusAt) <= 1000) return false;

  return true;
}

export default function FeedbackDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { user, isLoading: authLoading } = useAuth0();

  const inDashboard = location.pathname.startsWith("/dashboard");
  const basePath = inDashboard ? "/dashboard/feedback" : "/feedback";

  const {
    getPost,
    deletePost,
    changeStatus,
    createComment,
    updateComment,
    deleteComment,
  } = useFeedbackApi();

  const [post, setPost] = useState<FeedbackPostDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [statusBusy, setStatusBusy] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [commentBusyId, setCommentBusyId] = useState<string | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [commentDeleteId, setCommentDeleteId] = useState<string | null>(null);

  const [snack, setSnack] = useState<
    { msg: string; severity: "error" | "success" } | null
  >(null);

  /**
   * silent=true 면 전체 화면 로딩 게이트를 건너뛴다.
   *
   * 댓글 삭제처럼 이미 그려진 화면을 부분 갱신하려고 부르는 재조회에서
   * setLoading(true) 를 태우면 아래 `if (loading || authLoading)` 게이트에
   * 걸려 제목·본문·댓글이 통째로 사라졌다가 돌아온다 — 댓글 하나 지웠을 뿐인데
   * 페이지가 깜빡이고 스크롤이 맨 위로 튄다. 실패 처리(404/에러)는 두 모드가
   * 동일하다 — 조용한 재조회 중 글이 사라진 것도 알려야 하기 때문이다.
   */
  const fetchPost = useCallback(async (silent = false) => {
    if (!id) return;
    if (!silent) setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const dto = await getPost(id);
      setPost(dto);
    } catch (e: any) {
      // 삭제된 글(404)과 진짜 실패를 가른다 — 훅이 status 를 실어 보낸다.
      if (e?.status === 404) setNotFound(true);
      else setError(e?.message ?? "글을 불러오지 못했습니다.");
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  // 작성/수정 후 넘어온 플래시 메시지. 한 번 읽고 지운다 —
  // FeedbackListPage 의 삭제 플래시와 같은 방식이다. 작성 화면에서
  // setSnack 후 곧바로 navigate 하면 그 화면이 즉시 언마운트되어
  // 스낵바가 페인트될 기회 자체가 없기 때문에 도착지에서 띄운다.
  const flash = (location.state as { flash?: string } | null)?.flash;
  useEffect(() => {
    if (!flash) return;
    setSnack({ msg: flash, severity: "success" });
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flash]);

  /* ---------- 상태 변경 (누구나) ---------- */

  const handleStatusChange = async (next: FeedbackStatus) => {
    if (!post || !id) return;
    const previous = post;
    // 낙관적 갱신 — 칩이 즉시 바뀌고, 실패하면 되돌린다.
    setPost({ ...post, status: next });
    setStatusBusy(true);
    try {
      const updated = await changeStatus(id, next);
      // 서버가 정본이다. 동시에 다른 사람이 바꿨어도 응답으로 수렴한다.
      // 목록 응답에는 comments 가 없으므로 기존 댓글을 잃지 않게 병합한다.
      setPost((cur) => ({ ...updated, comments: cur?.comments ?? previous.comments }));
      setSnack({
        msg: `상태를 '${statusLabel(next)}'로 변경했습니다.`,
        severity: "success",
      });
    } catch (e: any) {
      setPost(previous); // 롤백
      setSnack({
        msg: e?.message ?? "상태를 변경하지 못했습니다.",
        severity: "error",
      });
    } finally {
      setStatusBusy(false);
    }
  };

  /* ---------- 글 삭제 (작성자만) ---------- */

  const confirmDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await deletePost(id);
      setDeleteOpen(false);
      // 목록에서 스낵바를 띄우기 위해 플래시를 실어 보낸다.
      navigate(basePath, { state: { flash: "삭제되었습니다." } });
    } catch (e: any) {
      setDeleting(false);
      setDeleteOpen(false);
      setSnack({ msg: e?.message ?? "삭제하지 못했습니다.", severity: "error" });
      await fetchPost(true); // 403/404 라면 실제 상태를 다시 읽어온다
    }
  };

  /* ---------- 댓글 ---------- */

  const handleCommentCreate = async (body: string) => {
    if (!id) return;
    setCommentSubmitting(true);
    try {
      const created = await createComment(id, { body });
      setPost((cur) =>
        cur
          ? {
              ...cur,
              comments: [...(cur.comments ?? []), created],
              commentCount: cur.commentCount + 1,
            }
          : cur
      );
      setSnack({ msg: "댓글을 등록했습니다.", severity: "success" });
    } catch (e: any) {
      setSnack({
        msg: e?.message ?? "댓글을 등록하지 못했습니다.",
        severity: "error",
      });
      throw e; // 입력값을 지우지 않도록 composer 에 실패를 알린다
    } finally {
      setCommentSubmitting(false);
    }
  };

  const handleCommentUpdate = async (commentId: string, body: string) => {
    if (!id) return;
    setCommentBusyId(commentId);
    try {
      const updated = await updateComment(id, commentId, { body });
      setPost((cur) =>
        cur
          ? {
              ...cur,
              comments: (cur.comments ?? []).map((c) =>
                c.id === commentId ? updated : c
              ),
            }
          : cur
      );
      setSnack({ msg: "댓글을 수정했습니다.", severity: "success" });
    } catch (e: any) {
      setSnack({
        msg: e?.message ?? "댓글을 수정하지 못했습니다.",
        severity: "error",
      });
      throw e;
    } finally {
      setCommentBusyId(null);
    }
  };

  const confirmCommentDelete = async () => {
    if (!id || !commentDeleteId) return;
    setCommentBusyId(commentDeleteId);
    try {
      await deleteComment(id, commentDeleteId);
      setCommentDeleteId(null);
      setSnack({ msg: "댓글을 삭제했습니다.", severity: "success" });
      // 카운트가 흔들리므로 서버 값으로 다시 맞춘다(화면은 유지한 채).
      await fetchPost(true);
    } catch (e: any) {
      setCommentDeleteId(null);
      setSnack({
        msg: e?.message ?? "댓글을 삭제하지 못했습니다.",
        severity: "error",
      });
    } finally {
      setCommentBusyId(null);
    }
  };

  /* ---------- 렌더 ---------- */

  const wrapperSx = {
    fontFamily: T.fonts.sans,
    p: inDashboard ? 0 : { xs: 2, md: 3 },
  } as const;

  const backButton = (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate(basePath)}
      sx={{ mb: 2 }}
    >
      목록으로
    </Button>
  );

  // user 와 post 가 둘 다 준비된 뒤에 헤더를 그린다 —
  // 그렇지 않으면 수정/삭제 버튼이 뒤늦게 나타나 깜빡인다.
  if (loading || authLoading) {
    return (
      <Box sx={wrapperSx}>
        {backButton}
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={wrapperSx}>
        {backButton}
        <Alert severity="warning">
          글을 찾을 수 없습니다. 삭제되었을 수 있습니다.
        </Alert>
      </Box>
    );
  }

  if (error || !post) {
    return (
      <Box sx={wrapperSx}>
        {backButton}
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => fetchPost()}>
              다시 시도
            </Button>
          }
        >
          {error ?? "글을 불러오지 못했습니다."}
        </Alert>
      </Box>
    );
  }

  const mySub = user?.sub;
  // 프론트 판정은 UI 힌트일 뿐이다 — 최종 방어선은 서버 403.
  const isMine = Boolean(mySub) && post.authorSub === mySub;
  const comments = post.comments ?? [];

  return (
    <Box sx={wrapperSx}>
      {backButton}

      {/* 헤더 */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "flex-start" },
              justifyContent: "space-between",
              gap: 1.5,
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FeedbackTypeChip type={post.category} size="medium" />
              <FeedbackStatusMenu
                value={post.status}
                onChange={handleStatusChange}
                disabled={statusBusy}
              />
            </Box>

            {isMine && (
              <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  startIcon={<EditIcon sx={{ fontSize: 18 }} />}
                  onClick={() => navigate(`${basePath}/edit/${post.id}`)}
                  sx={{ flex: { xs: 1, md: "none" } }}
                >
                  수정
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setDeleteOpen(true)}
                  sx={{ flex: { xs: 1, md: "none" } }}
                >
                  삭제
                </Button>
              </Box>
            )}
          </Box>

          <Typography
            component="h1"
            sx={{
              fontFamily: T.fonts.sans,
              fontSize: { xs: 20, md: 22 },
              fontWeight: 700,
              lineHeight: 1.35,
              letterSpacing: "-0.01em",
              color: C.ink,
              overflowWrap: "anywhere",
              wordBreak: "break-word",
            }}
          >
            {post.title}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontFamily: T.fonts.sans,
              fontSize: 13,
              color: C.sub,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {authorLabel(post)} · {fmtDateTime(post.createdTimestamp)}
            {wasEdited(post) ? " · 수정됨" : ""} · 댓글 {post.commentCount}
          </Typography>

          {post.statusChangedAt && (
            <Typography
              sx={{
                mt: 0.5,
                fontFamily: T.fonts.sans,
                fontSize: 12.5,
                color: C.caption,
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.6,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <FeedbackStatusChip status={post.status} />
              <Box component="span">
                {post.statusChangedByName?.trim()
                  ? `${post.statusChangedByName} · `
                  : ""}
                {fmtDateTime(post.statusChangedAt)} 변경
              </Box>
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* 본문 */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography
            component="div"
            sx={{
              fontFamily: T.fonts.sans,
              fontSize: 15,
              lineHeight: 1.75,
              color: C.ink,
              // 에러 로그·URL 붙여넣기가 잦다. 셋 중 하나라도 빠지면 긴 URL
              // 한 줄이 카드를 밀어 페이지 전체에 가로 스크롤이 생긴다.
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              maxWidth: "100%",
            }}
          >
            {post.body}
          </Typography>
        </CardContent>
      </Card>

      {/* 댓글 */}
      <Card>
        <CardContent>
          <Typography
            component="h2"
            sx={{
              fontFamily: T.fonts.sans,
              fontSize: 15,
              fontWeight: 700,
              color: C.ink,
              pb: 1,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            댓글 {post.commentCount}
          </Typography>

          <CommentList
            comments={comments}
            currentSub={mySub}
            onUpdate={handleCommentUpdate}
            onDelete={(commentId) => setCommentDeleteId(commentId)}
            busyId={commentBusyId}
          />

          <CommentComposer
            onSubmit={handleCommentCreate}
            submitting={commentSubmitting}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteOpen}
        title="글을 삭제하시겠습니까?"
        message="댓글도 함께 삭제됩니다. 이 작업은 되돌릴 수 없습니다."
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        loading={deleting}
        confirmText="삭제"
        cancelText="취소"
      />

      <ConfirmDialog
        open={Boolean(commentDeleteId)}
        title="댓글을 삭제하시겠습니까?"
        message="이 작업은 되돌릴 수 없습니다."
        onClose={() => setCommentDeleteId(null)}
        onConfirm={confirmCommentDelete}
        loading={Boolean(commentBusyId) && Boolean(commentDeleteId)}
        confirmText="삭제"
        cancelText="취소"
      />

      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert
            severity={snack.severity}
            variant="filled"
            onClose={() => setSnack(null)}
            sx={{ width: "100%", borderRadius: "8px", fontFamily: T.fonts.sans }}
          >
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
